/**
 * Shared Ad Context — the single source of truth that both image and video
 * generation can read from.
 */
export interface SharedAdContext {
  businessName: string;
  industry: string;
  offer: string;
  targetAudience: string;
  tone: string;
  style: string;
  creativeDirection: string;
  adGoal: string;
  adAngle: string;
  platform: string;
  location?: string;
  businessDescription?: string;
  servicesOffered?: string;
  brandPositioning?: string;
  uniqueDifferentiator?: string;
}

/**
 * Controls whether AI-generated video is allowed to contain visible text.
 * - "off"          → no text at all (default, recommended)
 * - "english_text" → allow real, legible English text only
 * Future: "overlay_only" can be added here without breaking existing logic.
 */
export type TextControlMode = 'off' | 'english_text';

export interface TextControlOptions {
  textControl: TextControlMode;
  exactText?: string;
}

/**
 * Returns the prompt instruction to append based on the text control setting.
 */
export function buildTextControlInstruction(options: TextControlOptions): string {
  const { textControl, exactText } = options;

  if (textControl === 'english_text') {
    const base =
      'If any text appears in the video, it must be real, correctly spelled English words only. ' +
      'Do not generate gibberish, distorted, unreadable, or nonsensical text. ' +
      'All lettering must be sharp, legible, and professionally designed.';

    if (exactText && exactText.trim()) {
      const sanitized = exactText.trim().replace(/"/g, '\\"');
      return (
        base +
        ` Include text that reads exactly: "${sanitized}". ` +
        'Spell it exactly as written with no extra characters, no misspellings, and no distorted letters.'
      );
    }
    return base;
  }

  // Default: "off" — hard no-text rule
  return (
    'Do not include any text, captions, signage, labels, lettering, numbers, or written words anywhere in the video.'
  );
}

// Runway Gen3 Turbo max prompt length — keep context portion well under this
// so the edge function can append the motion suffix without exceeding the limit.
const MAX_CONTEXT_CHARS = 300;

const NO_TEXT_GUIDANCE =
  'Use visual storytelling without readable captions, signage, logos, watermarks, numbers, or typography.';

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.lastIndexOf(' ', max);
  return cut > 0 ? text.slice(0, cut) : text.slice(0, max);
}

export function buildVideoPromptFromContext(
  ctx: SharedAdContext,
  textOptions?: TextControlOptions,
): string {
  const parts: string[] = [];

  // Core business + offer
  const businessLine = ctx.businessName
    ? `${ctx.businessName}${ctx.industry ? `, a ${ctx.industry} business` : ''}`
    : ctx.industry || 'this business';

  parts.push(`High-converting social media video ad for ${businessLine}.`);

  if (ctx.offer) {
    parts.push(`Offer: ${ctx.offer}.`);
  }

  if (ctx.targetAudience) {
    parts.push(`Audience: ${ctx.targetAudience}.`);
  }

  if (ctx.tone) {
    parts.push(`Tone: ${ctx.tone}.`);
  }

  if (ctx.adGoal) {
    const goalMotion = mapGoalToMotion(ctx.adGoal);
    if (goalMotion) parts.push(goalMotion);
  }

  if (ctx.adAngle) {
    const angleMotion = mapAngleToMotion(ctx.adAngle);
    if (angleMotion) parts.push(angleMotion);
  }

  const industryHint = getIndustryMotionHint(ctx.industry, ctx.offer);
  if (industryHint) parts.push(industryHint);

  if (ctx.creativeDirection) {
    parts.push(`Creative direction: ${ctx.creativeDirection}.`);
  }

  if (ctx.platform) {
    parts.push(`Optimise for ${ctx.platform}.`);
  }

  parts.push('Strong visual hook in first second. Premium, high-converting visual storytelling.');

  const context = truncate(parts.join(' '), MAX_CONTEXT_CHARS);

  // Determine text control instruction
  const resolvedTextOptions: TextControlOptions = textOptions ?? { textControl: 'off' };
  const textInstruction = buildTextControlInstruction(resolvedTextOptions);

  const prefix = resolvedTextOptions.textControl === 'off' ? `${NO_TEXT_GUIDANCE} ` : '';

  return `${prefix}${context} ${textInstruction}`;
}

function mapGoalToMotion(goal: string): string {
  const g = goal.toLowerCase();
  if (g.includes('lead')) return 'Drive urgency and lead generation through clear visual calls to action.';
  if (g.includes('traffic')) return 'Build curiosity and drive click-through with compelling visual storytelling.';
  if (g.includes('sale') || g.includes('conversion')) return 'Highlight the offer with strong visual emphasis on value and urgency.';
  if (g.includes('awareness')) return 'Build brand recognition through memorable visual moments and consistent identity.';
  if (g.includes('event')) return 'Create excitement and urgency around the event or promotion.';
  if (g.includes('app')) return 'Showcase the app experience with dynamic screen transitions and feature highlights.';
  if (g.includes('retarget')) return 'Re-engage viewers with familiar brand elements and a compelling new offer.';
  return '';
}

function mapAngleToMotion(angle: string): string {
  const a = angle.toLowerCase();
  if (a.includes('problem') || a.includes('solution')) {
    return 'Show the problem visually, then reveal the solution with a satisfying transformation.';
  }
  if (a.includes('before') || a.includes('after')) {
    return 'Use a clear before-to-after visual progression as the core narrative arc.';
  }
  if (a.includes('offer')) {
    return 'Lead with the offer — make it the visual centrepiece with bold, attention-grabbing presentation.';
  }
  if (a.includes('emotional')) {
    return 'Build emotional resonance through warm lighting, human moments, and authentic storytelling.';
  }
  if (a.includes('authority') || a.includes('expert')) {
    return 'Convey authority and trust through confident, polished visuals and professional presentation.';
  }
  return '';
}

function getIndustryMotionHint(industry: string, offer: string): string {
  if (!industry) return '';
  const i = industry.toLowerCase();
  const o = (offer || '').toLowerCase();

  if (i.includes('landscap') || i.includes('lawn') || i.includes('outdoor')) {
    return 'Show an overgrown space transforming into a clean, professionally maintained landscape.';
  }
  if (i.includes('plumb') || i.includes('hvac')) {
    return 'Show the relief and comfort of a problem solved — from frustration to a working, comfortable home.';
  }
  if (i.includes('clean')) {
    return 'Show a messy space transforming into a spotless, fresh environment.';
  }
  if (i.includes('restaurant') || i.includes('food')) {
    return 'Showcase the food with mouth-watering close-ups, steam, and vibrant colour.';
  }
  if (i.includes('fitness') || i.includes('gym')) {
    return 'Show energy, movement, and transformation — from effort to achievement.';
  }
  if (i.includes('real estate')) {
    return 'Showcase the property with sweeping camera movement, warm lighting, and aspirational lifestyle moments.';
  }
  if (i.includes('ecommerce') || i.includes('retail') || i.includes('product')) {
    return 'Highlight the product with dynamic close-ups, lifestyle context, and a clear value reveal.';
  }
  if (i.includes('legal')) {
    return 'Convey trust, professionalism, and resolution — from uncertainty to confident outcome.';
  }
  if (i.includes('healthcare') || i.includes('medical')) {
    return 'Show care, expertise, and positive outcomes with warm, reassuring visuals.';
  }
  if (i.includes('saas') || i.includes('software') || i.includes('tech')) {
    return 'Demonstrate the product in action with clean UI transitions and clear benefit moments.';
  }
  if (i.includes('beauty') || i.includes('salon')) {
    return 'Show the transformation — before and after — with glamorous, high-contrast visuals.';
  }
  if (i.includes('auto') || i.includes('mechanic')) {
    return 'Show the vehicle or repair with confident, professional motion and a clear result.';
  }
  if (o.includes('discount') || o.includes('off') || o.includes('sale')) {
    return 'Emphasise the deal with bold visual treatment and a sense of urgency.';
  }
  return '';
}
